import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  UserCog, Search, ArrowLeft, Folder, FileText, File as FileIcon, Image as ImgIcon,
  Download, Users, Loader2
} from "lucide-react";
import NavBar from "@/library/components/NavBar";
import Footer from "@/library/components/Footer";
import { STAFF_API } from "@/library/lib/staffApi";
import { resolveLogoUrl } from "@/library/lib/settings";
import axios from "axios";

const publicApi = axios.create({ baseURL: STAFF_API });

const ext_icon = (ext) => {
  const e = (ext || "").toLowerCase();
  if (["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(e)) return { Icon: ImgIcon, tone: "text-fuchsia-600 bg-fuchsia-50" };
  if (e === "pdf") return { Icon: FileText, tone: "text-rose-600 bg-rose-50" };
  if (["doc", "docx"].includes(e)) return { Icon: FileText, tone: "text-blue-600 bg-blue-50" };
  if (["xls", "xlsx", "csv"].includes(e)) return { Icon: FileText, tone: "text-emerald-600 bg-emerald-50" };
  if (["ppt", "pptx"].includes(e)) return { Icon: FileText, tone: "text-orange-600 bg-orange-50" };
  return { Icon: FileIcon, tone: "text-slate-600 bg-slate-100" };
};

const fmt_size = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export function StaffBrowse() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [globalResults, setGlobalResults] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async (query = "") => {
    setLoading(true);
    try {
      if (query) {
        const [{ data: userList }, { data: gr }] = await Promise.all([
          publicApi.get("/public/users", { params: { q: query } }),
          publicApi.get("/public/search", { params: { q: query } }),
        ]);
        setUsers(userList);
        setGlobalResults(gr);
      } else {
        const { data } = await publicApi.get("/public/users");
        setUsers(data);
        setGlobalResults(null);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(""); }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-8">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/10 text-gold-dark text-xs font-heading">
            <Users className="w-3.5 h-3.5" /> منسوبو المدرسة
          </div>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-black text-brand-dark tracking-tight">وثائق منسوبي المدرسة</h1>
          <div className="gold-line mx-auto mt-3" />
          <p className="mt-4 text-slate-600 text-sm sm:text-base">اطّلع على أعمال ووثائق منسوبي المدرسة — اختر منسوباً لعرض مجلداته وملفاته.</p>
        </div>

        <div className="mt-8 max-w-xl mx-auto relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && reload(q)} placeholder="بحث عن موظف، مسمى، ملف، أو مجلد..." data-testid="global-search" className="w-full pe-10 ps-4 py-3 rounded-xl bg-white border border-slate-200 shadow-sm focus:border-brand focus:ring-2 focus:ring-brand/15 outline-none text-sm" />
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 inline animate-spin mx-2" /> جارِ التحميل...</div>
        ) : (
          <>
            {globalResults && (globalResults.folders?.length > 0 || globalResults.files?.length > 0) && (
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                {globalResults.folders?.length > 0 && (
                  <div className="premium-card p-4">
                    <div className="font-heading font-bold text-brand-dark mb-3 flex items-center gap-2"><Folder className="w-4 h-4 text-gold-dark" /> مجلدات مطابقة ({globalResults.folders.length})</div>
                    <div className="space-y-1.5">
                      {globalResults.folders.map((f) => (
                        <Link key={f.id} to={`/library/staff/browse/${f.user_id}`} className="block px-3 py-2 rounded-lg hover:bg-brand/5 text-sm">
                          <span className="text-brand-dark font-heading">{f.name}</span>
                          <span className="text-slate-400 text-[11px] mr-2">— {f.owner?.full_name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                {globalResults.files?.length > 0 && (
                  <div className="premium-card p-4">
                    <div className="font-heading font-bold text-brand-dark mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-gold" /> ملفات مطابقة ({globalResults.files.length})</div>
                    <div className="space-y-1.5">
                      {globalResults.files.map((f) => (
                        <Link key={f.id} to={`/library/staff/browse/${f.user_id}`} className="block px-3 py-2 rounded-lg hover:bg-brand/5 text-sm">
                          <span className="text-brand-dark font-heading">{f.title}</span>
                          <span className="text-slate-400 text-[11px] mr-2">— {f.owner?.full_name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {users.length === 0 ? (
                <div className="col-span-full py-16 text-center text-slate-400">لا يوجد منسوبون مطابقون.</div>
              ) : users.map((u, i) => (
                <motion.div key={u.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.03 }}>
                  <Link to={`/library/staff/browse/${u.id}`} data-testid={`staff-card-${u.username}`} className="premium-card p-5 block group h-full">
                    <div className="flex items-start gap-3">
                      {u.photo_url ? (
                        <img src={resolveLogoUrl(u.photo_url)} alt="" className="w-16 h-16 rounded-xl object-cover ring-1 ring-slate-200 shrink-0" />
                      ) : (
                        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-brand/10 to-gold/10 flex items-center justify-center shrink-0"><UserCog className="w-7 h-7 text-brand" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-heading font-extrabold text-brand-dark text-[15px] leading-tight truncate">{u.full_name}</div>
                        <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full bg-brand/5 text-brand text-[10px] font-heading">{u.job_title}</div>
                        {u.department && <div className="mt-1 text-[11px] text-slate-500 truncate">{u.department}</div>}
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                      <div className="text-[11px] text-slate-500">
                        <span className="tabular-nums font-heading font-bold text-slate-800">{u.file_count}</span> ملف
                        <span className="mx-1 text-slate-300">·</span>
                        <span className="tabular-nums font-heading font-bold text-slate-800">{u.folder_count}</span> مجلد
                      </div>
                      <span className="text-[11px] font-heading text-gold-dark group-hover:text-gold-dark inline-flex items-center gap-1">
                        استعراض <ArrowLeft className="w-3 h-3" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Staff public detail: shows folders + files for the selected staff member
// -----------------------------------------------------------------------------
export function StaffBrowseDetail() {
  const { uid } = useParams();
  const [me, setMe] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [subfolders, setSubfolders] = useState([]);
  const [parentChain, setParentChain] = useState([]);
  const [files, setFiles] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!uid) return;
    (async () => {
      const [{ data: u }, { data: fs }] = await Promise.all([
        publicApi.get(`/public/users/${uid}`),
        publicApi.get(`/public/users/${uid}/folders`),
      ]);
      setMe(u); setFolders(fs);
    })();
  }, [uid]);

  const navigateToFolder = async (folder, chain) => {
    setSelectedFolder(folder);
    setParentChain(chain);
    const [subs, filesRes] = await Promise.all([
      publicApi.get(`/public/users/${uid}/folders`, { params: { parent_id: folder.id } }),
      publicApi.get(`/public/users/${uid}/files`, { params: { folder_id: folder.id, q: q || undefined } }),
    ]);
    setSubfolders(subs.data);
    setFiles(filesRes.data);
  };

  const openFolder = async (f) => {
    await navigateToFolder(f, []);
  };

  const enterSubfolder = async (sub) => {
    await navigateToFolder(sub, [...parentChain, selectedFolder]);
  };

  const goBack = async () => {
    if (parentChain.length > 0) {
      const prev = parentChain[parentChain.length - 1];
      await navigateToFolder(prev, parentChain.slice(0, -1));
    } else {
      setSelectedFolder(null); setSubfolders([]); setFiles([]); setParentChain([]);
    }
  };

  const doSearch = async () => {
    if (!selectedFolder) return;
    const { data } = await publicApi.get(`/public/users/${uid}/files`, { params: { folder_id: selectedFolder.id, q: q || undefined } });
    setFiles(data);
  };

  const openFile = (f) => window.open(`${STAFF_API}/public/files/${f.id}/download`, "_blank");

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-1 max-w-[1400px] mx-auto w-full px-4 sm:px-6 py-8">
        <Link to="/library/staff/browse" className="text-xs text-slate-500 hover:text-brand font-heading inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-3 h-3 rotate-180" /> العودة لقائمة المنسوبين</Link>

        {me && (
          <div className="premium-card p-5 sm:p-6 flex flex-col sm:flex-row items-center sm:items-start gap-5">
            <div className="shrink-0">
              {me.photo_url ? (
                <img src={resolveLogoUrl(me.photo_url)} alt={me.full_name} className="w-24 h-24 rounded-2xl object-cover ring-2 ring-gold/30 shadow-md" />
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-brand/10 to-gold/10 flex items-center justify-center"><UserCog className="w-10 h-10 text-brand" /></div>
              )}
            </div>
            <div className="flex-1 text-center sm:text-right">
              <h1 className="font-heading text-2xl font-extrabold text-brand-dark">{me.full_name}</h1>
              <div className="mt-1.5 flex flex-wrap justify-center sm:justify-start gap-2 text-slate-600 text-sm">
                <span className="inline-flex items-center gap-1 bg-brand/5 text-brand px-2 py-0.5 rounded-full text-xs font-heading">{me.job_title}</span>
                {me.department && <span className="text-xs text-slate-500">{me.department}</span>}
              </div>
              <div className="mt-3 text-xs text-slate-500">
                <span className="tabular-nums font-heading font-bold text-brand-dark">{me.file_count}</span> ملف
                <span className="mx-2 text-slate-300">·</span>
                <span className="tabular-nums font-heading font-bold text-brand-dark">{me.folder_count}</span> مجلد
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
          <div className="premium-card p-4">
            <div className="font-heading font-bold text-brand-dark flex items-center gap-1.5 mb-3"><Folder className="w-4 h-4" /> المجلدات ({folders.length})</div>
            <div className="space-y-1.5 max-h-[520px] overflow-y-auto">
              {folders.length === 0 && <div className="text-xs text-slate-400 text-center py-4">لا توجد مجلدات.</div>}
              {folders.map((f) => (
                <button key={f.id} onClick={() => openFolder(f)} data-testid={`browse-folder-${f.id}`} className={`w-full text-right rounded-lg px-2.5 py-2 flex items-center gap-2 transition-colors ${selectedFolder?.id === f.id && parentChain.length === 0 ? "bg-brand/10 border border-brand/30" : "hover:bg-slate-50 border border-transparent"}`}>
                  <Folder className={`w-4 h-4 ${selectedFolder?.id === f.id ? "text-brand" : "text-gold-dark"}`} />
                  <span className="text-sm text-slate-800 truncate flex-1">{f.name}</span>
                  <span className="text-[10px] text-slate-400 tabular-nums">{f.file_count}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="premium-card p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-4">
              <div className="font-heading font-bold text-brand-dark flex items-center gap-2 min-w-0">
                {selectedFolder ? (
                  <>
                    {parentChain.length > 0 && (
                      <button onClick={goBack} className="p-1 rounded-lg hover:bg-slate-100 text-slate-600" title="رجوع">
                        <ArrowLeft className="w-4 h-4" />
                      </button>
                    )}
                    <Folder className="w-4 h-4 text-gold-dark shrink-0" />
                    <span className="truncate">
                      {parentChain.map(p => p.name).join(" / ")}{parentChain.length > 0 ? " / " : ""}{selectedFolder.name}
                    </span>
                  </>
                ) : <><FileText className="w-4 h-4 text-gold" /> اختر مجلداً</>}
              </div>
              {selectedFolder && (
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()} placeholder="بحث في الملفات..." className="pe-8 ps-3 py-1.5 rounded-lg border border-slate-200 text-xs w-52 focus:border-brand outline-none" />
                </div>
              )}
            </div>

            {!selectedFolder ? (
              <div className="py-16 text-center text-slate-400"><Folder className="w-14 h-14 mx-auto text-slate-200" /><p className="mt-3 text-sm">اختر مجلداً من الجانب لعرض ملفاته.</p></div>
            ) : subfolders.length === 0 && files.length === 0 ? (
              <div className="py-16 text-center text-slate-400"><FileIcon className="w-14 h-14 mx-auto text-slate-200" /><p className="mt-3 text-sm">هذا المجلد فارغ.</p></div>
            ) : (
              <>
                {subfolders.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-4">
                    {subfolders.map((sub) => (
                      <button key={sub.id} onClick={() => enterSubfolder(sub)} data-testid={`browse-subfolder-${sub.id}`} className="rounded-lg border border-slate-200 hover:border-brand/40 hover:bg-brand/5 transition-colors p-2.5 flex items-center gap-2 text-right">
                        <Folder className="w-4 h-4 text-gold-dark shrink-0" />
                        <span className="text-sm text-slate-800 truncate flex-1">{sub.name}</span>
                        <span className="text-[10px] text-slate-400 tabular-nums">{sub.file_count}</span>
                      </button>
                    ))}
                  </div>
                )}
                {files.length > 0 && (
              <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                {files.map((f) => {
                  const { Icon, tone } = ext_icon(f.extension);
                  return (
                    <button key={f.id} onClick={() => openFile(f)} data-testid={`browse-file-${f.id}`} className="premium-card p-4 text-right group">
                      <div className="flex items-start gap-3">
                        {f.thumb_path ? (
                          <img src={`${STAFF_API}/public/files/${f.id}/thumb`} alt="" className="w-14 h-16 object-cover rounded-md ring-1 ring-slate-200 shrink-0" />
                        ) : (
                          <div className={`w-14 h-16 rounded-md flex items-center justify-center shrink-0 ${tone}`}><Icon className="w-6 h-6" /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-heading font-bold text-brand-dark text-sm leading-snug truncate" title={f.title}>{f.title}</div>
                          {f.description && <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{f.description}</div>}
                          <div className="mt-1.5 flex items-center gap-2 text-[10px] text-slate-400 tabular-nums">
                            <span className="uppercase font-mono">{f.extension}</span>
                            <span>·</span><span>{fmt_size(f.size)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 text-xs font-heading text-brand flex items-center justify-center gap-1 py-1.5 bg-brand/5 rounded-lg group-hover:bg-brand/10">
                        <Download className="w-3.5 h-3.5" /> فتح / تنزيل
                      </div>
                    </button>
                  );
                })}
              </div>
              </>
                )}
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
